/**
 * What a tab remembers across a reconnect or a reload: the peer id it was
 * given plus the token that lets it claim the id again, and the membership
 * claim handed over on admission.
 *
 * sessionStorage on purpose. It is per tab and dies with the tab, which is
 * exactly a session; two tabs are two participants. (A duplicated tab copies
 * it; the server refuses to hand a live id to a second socket, and the copy
 * gets a fresh one.)
 *
 * The store is passed in (the SDK client's `session` storage), so tests,
 * server renders and native shells can hand in their own or none.
 */

import type { KeyValueStore } from './storage.js'

export interface ResumeCredentials {
  peerId: string
  token: string
}

export interface Membership {
  roomId: string
  role: string
  token: string
}

const RESUME_KEY = 'ls_resume'
const MEMBERSHIP_KEY = 'ls_membership'

type Store = KeyValueStore

function read<T>(store: Store | null, key: string, valid: (v: unknown) => v is T): T | null {
  try {
    const raw = store?.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return valid(parsed) ? parsed : null
  } catch {
    return null
  }
}

function write(store: Store | null, key: string, value: unknown): void {
  try {
    if (value === null) store?.removeItem(key)
    else store?.setItem(key, JSON.stringify(value))
  } catch {
    // Not remembered. The next connection simply gets a fresh identity.
  }
}

const isString = (v: unknown): v is string => typeof v === 'string' && v.length > 0

function isResume(v: unknown): v is ResumeCredentials {
  return (
    typeof v === 'object' &&
    v !== null &&
    isString((v as ResumeCredentials).peerId) &&
    isString((v as ResumeCredentials).token)
  )
}

function isMembership(v: unknown): v is Membership {
  return (
    typeof v === 'object' &&
    v !== null &&
    isString((v as Membership).roomId) &&
    isString((v as Membership).role) &&
    isString((v as Membership).token)
  )
}

export function loadResume(store: Store | null): ResumeCredentials | null {
  return read(store, RESUME_KEY, isResume)
}

export function saveResume(credentials: ResumeCredentials, store: Store | null): void {
  write(store, RESUME_KEY, credentials)
}

export function clearResume(store: Store | null): void {
  write(store, RESUME_KEY, null)
}

export function loadMembership(store: Store | null): Membership | null {
  return read(store, MEMBERSHIP_KEY, isMembership)
}

export function saveMembership(membership: Membership, store: Store | null): void {
  write(store, MEMBERSHIP_KEY, membership)
}

export function clearMembership(store: Store | null): void {
  write(store, MEMBERSHIP_KEY, null)
}
